import React from 'react';
import { Button, Group } from '@inexture/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput, Textarea } from '@/form/elements/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { closeAllModals } from '@inexture/modals';
import {
  companyFormSchema,
  CompanyFormT,
  companyFormDefaultValues
} from './company.schema';

interface CompanyFormProps {
  defaultValues?: Partial<CompanyFormT>;
  onSubmit: (data: CompanyFormT) => void | Promise<void>;
  isLoading?: boolean;
  submitButtonText?: string;
  showCancelButton?: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  defaultValues = {},
  onSubmit,
  isLoading = false,
  submitButtonText = 'Save',
  showCancelButton = false
}) => {
  const methods = useForm<CompanyFormT>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      ...companyFormDefaultValues,
      ...defaultValues
    }
  });

  React.useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      methods.reset({
        ...companyFormDefaultValues,
        ...defaultValues
      });
    }
  }, [defaultValues, methods]);

  const handleSubmit = async (data: CompanyFormT) => {
    await onSubmit(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <div className="grid grid-rows-1 gap-2">
          <div className="grid grid-cols-2 gap-2">
            <TextInput
              name="first_name"
              label="First Name"
              props={{
                placeholder: 'Enter first name',
                withAsterisk: true
              }}
            />
            <TextInput
              name="last_name"
              label="Last Name"
              props={{
                placeholder: 'Enter last name',
                withAsterisk: true
              }}
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <TextInput
              name="company_name"
              label="Company Name"
              props={{
                placeholder: 'Enter company name',
                withAsterisk: true
              }}
            />

            <TextInput
              name="company_slug"
              label="Company Slug"
              props={{
                placeholder: 'Enter company slug',
                withAsterisk: true
              }}
            />

            <TextInput
              name="company_registration"
              label="Company Registration"
              props={{
                placeholder: 'Enter company registration',
                withAsterisk: true
              }}
            />

            <TextInput
              name="email"
              label="Email"
              props={{
                placeholder: 'Enter email address',
                withAsterisk: true,
                type: 'email'
              }}
            />

            <TextInput
              name="contact"
              label="Contact Number"
              props={{
                placeholder: 'Enter contact number',
                withAsterisk: true,
                type: 'tel'
              }}
            />

            <Textarea
              name="registration_comments"
              label="Comments"
              props={{
                placeholder: 'Enter comments'
              }}
            />

            <TextInput
              name="mobile"
              label="SMS Number"
              props={{
                placeholder: 'Enter mobile number',
                type: 'tel'
              }}
            />
          </div>
        </div>

        <Group justify="flex-end" mt="xl">
          {showCancelButton && (
            <Button
              variant="outline"
              size="md"
              onClick={() => closeAllModals()}
              type="button"
            >
              Cancel
            </Button>
          )}
          <Button size="md" type="submit" loading={isLoading}>
            {submitButtonText}
          </Button>
        </Group>
      </form>
    </FormProvider>
  );
};

export default CompanyForm;
